import React, {useEffect, useRef, useState} from 'react';
import NavPanel from "../components/NavPanel";
import BackButton from "../components/BackButton";
import '../styles/Admin.css';
import '../styles/AdminPayees.css';
import {URLS} from "../utils/urls";
import {REACT_APP_API_URL} from "../config";
import {
    payeeCreateApi,
    payeeDeleteApi,
    payeesGetApi,
    payeeSetDefaultApi,
    payeeUpdateApi,
    payeeUploadImageApi,
} from "../http/payeeAPI";
import {
    calcTablesGetApi,
    calcTableCreateApi,
    calcTableUpdateApi,
    calcTableActivateApi,
    calcTableArchiveApi,
    calcTableUnarchiveApi,
    calcTableDeleteApi,
} from "../http/calcTableAPI";
import CalculationTable, {
    CalcConfig,
    CalcFooterField,
    CalcSchemaColumn,
    DEFAULT_CALC_SCHEMA,
    getEffectiveSchema,
    getEffectiveFooter,
    configOfCalcTable,
    validateSchema,
    validateFooter,
    isValidColumnKey,
    recalcAllRows,
    FORMULA_CONSTS,
} from "../components/CalculationTable";
import locale, {translateApiMessage} from "../locale";


type PayeeType = {
    id: number,
    name: string,
    entityType: string | null,
    edrpou: string | null,
    vatId: string | null,
    iban: string | null,
    bankName: string | null,
    address: string | null,
    defaultContract: string | null,
    signatureLine: string | null,
    logoFile: string | null,
    stampFile: string | null,
    signFile: string | null,
    // Ширина печати / подписи (px) в счёте. null → дефолт на сервере.
    stampWidthPx: number | null,
    signWidthPx: number | null,
    isVatPayer: boolean,
    isDefault: boolean,
    invoiceCounter: number,
    // Legacy: the schema/rates that used to live here now live in CalcTable
    // (`currentCalcTable`); kept only so calculations that predate that
    // feature keep resolving. No longer editable from this page.
    calcConfig: CalcConfig | null,
    currentCalcTable: CalcTableType | null,
};

// Which schema column of this table feeds each cell of the invoice products
// table. Values are column keys; missing → server falls back to the schema's
// `invoice*` roles, then to the default a/b/c/g/h/i columns.
type InvoiceColumns = {
    name?: string, qty?: string, unit?: string,
    price?: string, vat?: string, total?: string,
};

const INVOICE_SLOTS: Array<{ key: keyof InvoiceColumns, label: string }> = [
    { key: 'name', label: 'Наименование' },
    { key: 'qty', label: 'Количество' },
    { key: 'unit', label: 'Ед. измерения' },
    { key: 'price', label: 'Цена без ПДВ' },
    { key: 'vat', label: 'ПДВ' },
    { key: 'total', label: 'Сумма с ПДВ' },
];

type CalcTableType = {
    id: number,
    payeeId: number,
    name: string,
    config: CalcConfig,
    invoiceColumns: InvoiceColumns | null,
    hiddenColumns: string[] | null,
    isArchived: boolean,
    hasUsage?: boolean,
    createdAt?: string,
};

type FormState = {
    name: string,
    entityType: string,
    edrpou: string,
    vatId: string,
    iban: string,
    bankName: string,
    address: string,
    defaultContract: string,
    signatureLine: string,
    invoiceCounter: string,
    isVatPayer: boolean,
    // Ширина печати / подписи (px) в счёте; '' → серверный дефолт. Не в списке
    // FIELDS — редактируется прямо в блоке с картинками, а не в сетке реквизитов.
    stampWidthPx: string,
    signWidthPx: string,
};

// Editor state for creating a new calc table, or editing one that has no
// linked calculations yet (only `name` stays editable once a table is used —
// see `submitTable`).
type TableEditorState = {
    id?: number,
    name: string,
    vatRate: string,
    epRate: string,
    militaryRate: string,
    schema: CalcSchemaColumn[],
    footer: CalcFooterField[],
    // Presentation-only invoice-column mapping — editable even when `locked`.
    invoiceColumns: InvoiceColumns,
    // Column visibility lives in each schema column's `hidden` flag here, but is
    // saved as CalcTable.hiddenColumns — presentation-only, editable even when `locked`.
    locked: boolean, // true = has linked calculations; only `name`, `invoiceColumns` + visibility may be saved
};

const cloneArr = <T,>(a: T[]): T[] => JSON.parse(JSON.stringify(a));
const cloneSchema = (s: CalcSchemaColumn[]): CalcSchemaColumn[] => cloneArr(s);

// Footer-total roles the editor lets you (re)assign; invoice* roles are left
// untouched on whichever columns already carry them.
const TOTAL_ROLES: Array<{ value: string, label: string }> = [
    { value: '', label: locale.payeeAdmin.tableEditor.totalRoleOptions.none },
    { value: 'expensesWithVat', label: locale.payeeAdmin.tableEditor.totalRoleOptions.expensesWithVat },
    { value: 'incomeWithVat', label: locale.payeeAdmin.tableEditor.totalRoleOptions.incomeWithVat },
    { value: 'profit', label: locale.payeeAdmin.tableEditor.totalRoleOptions.profit },
];
const TOTAL_ROLE_SET = new Set(TOTAL_ROLES.map((r) => r.value).filter(Boolean));

const nextColumnKey = (schema: CalcSchemaColumn[]): string => {
    const used = new Set(schema.map((c) => c.key));
    for (let i = 0; i < 26; i++) {
        const k = String.fromCharCode(97 + i);
        if (isValidColumnKey(k) && !used.has(k)) return k;
    }
    for (let n = 1; n < 999; n++) {
        const k = `c${n}`;
        if (!used.has(k)) return k;
    }
    return `c${Date.now()}`;
};

const nextFooterKey = (footer: CalcFooterField[]): string => {
    const used = new Set(footer.map((f) => f.key));
    for (let n = 1; n < 999; n++) {
        const k = `total${n}`;
        if (!used.has(k)) return k;
    }
    return `total${Date.now()}`;
};

const EMPTY_FORM: FormState = {
    name: '', entityType: 'ФОП', edrpou: '', vatId: '', iban: '',
    bankName: '', address: '', defaultContract: 'Основний договір', signatureLine: '',
    invoiceCounter: '0', isVatPayer: true,
    stampWidthPx: '', signWidthPx: '',
};

// A brand-new table starts with the same schema + footer the app has always
// used by default, so the preview isn't blank — both stay fully editable.
const EMPTY_TABLE_EDITOR: TableEditorState = {
    name: '', vatRate: '20', epRate: '3', militaryRate: '1',
    schema: cloneSchema(DEFAULT_CALC_SCHEMA),
    footer: getEffectiveFooter(null, DEFAULT_CALC_SCHEMA),
    invoiceColumns: {},
    locked: false,
};

// Tax rates that drive a calculation table. Percent values.
const TABLE_RATE_FIELDS: Array<{ key: 'vatRate' | 'epRate' | 'militaryRate', label: string }> = [
    { key: 'vatRate', label: locale.payeeAdmin.tableEditor.rateFields.vatRate },
    { key: 'epRate', label: locale.payeeAdmin.tableEditor.rateFields.epRate },
    { key: 'militaryRate', label: locale.payeeAdmin.tableEditor.rateFields.militaryRate },
];

// maxLength mirrors the actual DB column width (see Payee.init in
// crmServer/src/models/models.ts) — without this, a value that's fine
// client-side can still 500 at INSERT time ("value too long for type
// character varying(N)"). Keep the two in sync if a column width changes.
const FIELDS: Array<{ key: keyof FormState, label: string, placeholder?: string, type?: string, hint?: string, warn?: string, maxLength?: number }> = [
    { key: 'name', ...locale.payeeAdmin.fields.name, maxLength: 256 },
    { key: 'entityType', ...locale.payeeAdmin.fields.entityType, maxLength: 32 },
    { key: 'edrpou', ...locale.payeeAdmin.fields.edrpou, maxLength: 16 },
    { key: 'vatId', ...locale.payeeAdmin.fields.vatId, maxLength: 16 },
    { key: 'iban', ...locale.payeeAdmin.fields.iban, maxLength: 64 },
    { key: 'bankName', ...locale.payeeAdmin.fields.bankName, maxLength: 256 },
    { key: 'address', ...locale.payeeAdmin.fields.address, maxLength: 512 },
    { key: 'defaultContract', ...locale.payeeAdmin.fields.defaultContract, maxLength: 256 },
    { key: 'signatureLine', ...locale.payeeAdmin.fields.signatureLine, maxLength: 256 },
    {
        key: 'invoiceCounter', type: 'number',
        ...locale.payeeAdmin.fields.invoiceCounter,
    },
];

const toForm = (p: PayeeType): FormState => ({
    name: p.name ?? '',
    entityType: p.entityType ?? '',
    edrpou: p.edrpou ?? '',
    vatId: p.vatId ?? '',
    iban: p.iban ?? '',
    bankName: p.bankName ?? '',
    address: p.address ?? '',
    defaultContract: p.defaultContract ?? '',
    signatureLine: p.signatureLine ?? '',
    invoiceCounter: `${p.invoiceCounter ?? 0}`,
    isVatPayer: !!p.isVatPayer,
    stampWidthPx: p.stampWidthPx != null ? `${p.stampWidthPx}` : '',
    signWidthPx: p.signWidthPx != null ? `${p.signWidthPx}` : '',
});

const mediaUrl = (file: string | null) =>
    file ? `${REACT_APP_API_URL}media/${file}?v=${Date.now()}` : null;


const AdminPayees = () => {
    const [payees, setPayees] = useState<Array<PayeeType>>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [isCreating, setIsCreating] = useState<boolean>(false);
    const [form, setForm] = useState<FormState>(EMPTY_FORM);
    const [confirmDelete, setConfirmDelete] = useState<boolean>(false);
    const [msg, setMsg] = useState<{ text: string, type: 'error' | 'success' } | null>(null);
    const [reload, setReload] = useState<number>(0);

    const [calcTables, setCalcTables] = useState<Array<CalcTableType>>([]);
    const [tableFilter, setTableFilter] = useState<'all' | 'current' | 'archived'>('all');
    const [tableEditor, setTableEditor] = useState<TableEditorState | null>(null);
    const [confirmDeleteTableId, setConfirmDeleteTableId] = useState<number | null>(null);

    const logoInput = useRef<HTMLInputElement>(null);
    const stampInput = useRef<HTMLInputElement>(null);
    const signInput = useRef<HTMLInputElement>(null);

    const selected = payees.find((p) => p.id === selectedId) ?? null;

    useEffect(() => {
        payeesGetApi().then((res) => {
            const list: Array<PayeeType> = Array.isArray(res?.data) ? res.data : [];
            setPayees(list);
            setConfirmDelete(false);
            setSelectedId((prev) => {
                if (prev && list.some((p) => p.id === prev)) return prev;
                return list.length ? list[0].id : null;
            });
        });
    }, [reload]);

    useEffect(() => {
        if (isCreating) {
            setForm(EMPTY_FORM);
        } else if (selected) {
            setForm(toForm(selected));
        }
        setConfirmDelete(false);
    }, [selectedId, isCreating]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (isCreating || !selectedId) {
            setCalcTables([]);
            return;
        }
        calcTablesGetApi(selectedId).then((res) => {
            setCalcTables(Array.isArray(res?.data) ? res.data : []);
        });
        setTableEditor(null);
        setConfirmDeleteTableId(null);
        setTableFilter('all');
    }, [selectedId, isCreating, reload]);

    // The live preview only rebuilds the table when the schema/footer *shape*
    // changes (see CalculationTable's renderTable signature) — a rate-only
    // edit needs an explicit recalc to show up on already-typed-in rows.
    useEffect(() => {
        if (!tableEditor) return;
        recalcAllRows({ Key: 'payeeTablePreview' });
    }, [tableEditor?.vatRate, tableEditor?.epRate, tableEditor?.militaryRate]); // eslint-disable-line react-hooks/exhaustive-deps

    const flash = (text: string, type: 'error' | 'success') => {
        setMsg({ text, type });
        setTimeout(() => setMsg(null), 4000);
    };

    // Backend error text is inconsistently English/Ukrainian/Russian across
    // controllers/eras (see CLAUDE.md) — this local "flash" banner is a
    // second, immediate feedback path alongside the global toast (http/
    // index.ts), so it needs the same translation pass before display.
    const readErr = (res: any, fallback: string) => {
        const data = res?.response?.data;
        if (data?.problems?.length) return translateApiMessage(data.problems.join('; '));
        return data?.message ? translateApiMessage(data.message) : fallback;
    };

    const setField = (key: keyof FormState, value: string | boolean) =>
        setForm((f) => ({ ...f, [key]: value }));

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.name.trim()) {
            flash(locale.payeeAdmin.validation.nameRequired, 'error');
            return;
        }
        if (!isCreating && selected && Number(form.invoiceCounter) < selected.invoiceCounter) {
            flash(locale.payeeAdmin.validation.invoiceCounterMustIncrease(selected.invoiceCounter), 'error');
            return;
        }

        const res = isCreating
            ? await payeeCreateApi(form)
            : await payeeUpdateApi(selectedId as number, form);

        if (res?.response?.status >= 400) {
            flash(readErr(res, locale.payeeAdmin.validation.saveFailed), 'error');
            return;
        }
        const saved: PayeeType | undefined = res?.data;
        setIsCreating(false);
        if (saved?.id) setSelectedId(saved.id);
        setReload((n) => n + 1);
        flash(locale.payeeAdmin.validation.saved, 'success');
    };

    // ── Calc table editor (create new / edit an unused one) ─────────────────

    const openCreateTable = () => setTableEditor({
        ...EMPTY_TABLE_EDITOR,
        schema: cloneSchema(DEFAULT_CALC_SCHEMA),
        footer: cloneArr(EMPTY_TABLE_EDITOR.footer),
    });

    const openEditTable = (t: CalcTableType) => {
        const schema = cloneSchema(getEffectiveSchema(configOfCalcTable(t)));
        setTableEditor({
            id: t.id,
            name: t.name,
            vatRate: `${t.config?.vatRate ?? 20}`,
            epRate: `${t.config?.epRate ?? 3}`,
            militaryRate: `${t.config?.militaryRate ?? 1}`,
            schema,
            footer: cloneArr(getEffectiveFooter(t.config, schema)),
            invoiceColumns: { ...(t.invoiceColumns || {}) },
            locked: !!t.hasUsage,
        });
    };

    const mutateTableSchema = (fn: (s: CalcSchemaColumn[]) => CalcSchemaColumn[]) =>
        setTableEditor((f) => (f ? { ...f, schema: fn(cloneSchema(f.schema)) } : f));

    const setTableCol = (idx: number, patch: Partial<CalcSchemaColumn>) =>
        mutateTableSchema((s) => { s[idx] = { ...s[idx], ...patch }; return s; });

    const setTableColTotalRole = (idx: number, role: string) =>
        mutateTableSchema((s) => {
            const kept = (s[idx].roles || []).filter((r) => !TOTAL_ROLE_SET.has(r));
            const roles = role ? [...kept, role as any] : kept;
            s[idx] = { ...s[idx], roles: roles.length ? roles : undefined };
            return s;
        });

    const setTableInvoiceCol = (slot: keyof InvoiceColumns, key: string) =>
        setTableEditor((f) => f && ({
            ...f,
            invoiceColumns: { ...f.invoiceColumns, [slot]: key || undefined },
        }));

    const moveTableCol = (idx: number, dir: -1 | 1) =>
        mutateTableSchema((s) => {
            const j = idx + dir;
            if (j < 0 || j >= s.length) return s;
            [s[idx], s[j]] = [s[j], s[idx]];
            return s;
        });

    const addTableCol = () =>
        mutateTableSchema((s) => [...s, {
            key: nextColumnKey(s), label: locale.payeeAdmin.tableEditor.newColumnDefaultLabel, kind: 'input', inputType: 'number',
        }]);

    const removeTableCol = (idx: number) =>
        mutateTableSchema((s) => (s.length > 1 ? s.filter((_, i) => i !== idx) : s));

    const resetTableSchema = () => mutateTableSchema(() => cloneSchema(DEFAULT_CALC_SCHEMA));

    const tableSchemaProblems = tableEditor ? validateSchema(tableEditor.schema) : [];

    // ── Footer (summary) fields editor ───────────────────────────────────────

    const mutateTableFooter = (fn: (f: CalcFooterField[]) => CalcFooterField[]) =>
        setTableEditor((f) => (f ? { ...f, footer: fn(cloneArr(f.footer)) } : f));

    const setFooterField = (idx: number, patch: Partial<CalcFooterField>) =>
        mutateTableFooter((s) => { s[idx] = { ...s[idx], ...patch }; return s; });

    const moveFooterField = (idx: number, dir: -1 | 1) =>
        mutateTableFooter((s) => {
            const j = idx + dir;
            if (j < 0 || j >= s.length) return s;
            [s[idx], s[j]] = [s[j], s[idx]];
            return s;
        });

    const addFooterField = () => {
        // Default the new field's "sum" column to the first numeric column —
        // summing a text column (e.g. "Товар") is never what's wanted.
        const schema = tableEditor?.schema ?? [];
        const numericCol = schema.find((c) => c.kind === 'formula' || c.inputType !== 'text');
        mutateTableFooter((s) => [...s, {
            key: nextFooterKey(s), label: locale.payeeAdmin.footerEditor.newFieldDefaultLabel, kind: 'sum', column: (numericCol ?? schema[0])?.key,
        }]);
    };

    const removeFooterField = (idx: number) =>
        mutateTableFooter((s) => s.filter((_, i) => i !== idx));

    const tableFooterProblems = tableEditor ? validateFooter(tableEditor.schema, tableEditor.footer) : [];

    const submitTable = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!tableEditor || !selectedId) return;
        if (!tableEditor.name.trim()) {
            flash(locale.payeeAdmin.tables.validation.nameRequired, 'error');
            return;
        }
        if (!tableEditor.locked && tableSchemaProblems.length) {
            flash(locale.payeeAdmin.tables.validation.schemaErrors(tableSchemaProblems.join('; ')), 'error');
            return;
        }
        if (!tableEditor.locked && tableFooterProblems.length) {
            flash(locale.payeeAdmin.tables.validation.footerErrors(tableFooterProblems.join('; ')), 'error');
            return;
        }

        const payload: any = { name: tableEditor.name };
        if (!tableEditor.id) payload.payeeId = selectedId;
        if (!tableEditor.locked) {
            payload.vatRate = Number(tableEditor.vatRate) || 0;
            payload.epRate = Number(tableEditor.epRate) || 0;
            payload.militaryRate = Number(tableEditor.militaryRate) || 0;
            payload.schema = tableEditor.schema;
            payload.footer = tableEditor.footer;
        }
        // Invoice-column mapping is presentation-only and always sent — the
        // server accepts it even for a locked table and drops keys that don't
        // match a column of the (effective) schema.
        const invoiceColumns: { [k: string]: string } = {};
        for (const { key } of INVOICE_SLOTS) {
            const v = tableEditor.invoiceColumns[key];
            if (v) invoiceColumns[key] = v;
        }
        payload.invoiceColumns = invoiceColumns;
        // Visibility too — always sent as the full list, so it overrides the
        // schema's own `hidden` flags from now on.
        payload.hiddenColumns = tableEditor.schema.filter((c) => c.hidden).map((c) => c.key);

        const res = tableEditor.id
            ? await calcTableUpdateApi(tableEditor.id, payload)
            : await calcTableCreateApi(payload);

        // http/*API.ts wrappers return the raw caught error on failure instead
        // of throwing — a network-level failure (no server response at all)
        // has neither `.response.status` nor `.data`, so checking only the
        // former would show "сохранено" for a request that never landed.
        if (!res?.data || res?.response?.status >= 400) {
            flash(readErr(res, locale.payeeAdmin.tables.validation.saveFailed), 'error');
            return;
        }
        setTableEditor(null);
        setReload((n) => n + 1);
        flash(locale.payeeAdmin.tables.validation.saved, 'success');
    };

    const activateTable = async (id: number) => {
        const res = await calcTableActivateApi(id);
        if (res?.response?.status >= 400) {
            flash(readErr(res, locale.payeeAdmin.tables.validation.activateFailed), 'error');
            return;
        }
        setReload((n) => n + 1);
        flash(locale.payeeAdmin.tables.validation.activated, 'success');
    };

    const archiveTable = async (id: number) => {
        const res = await calcTableArchiveApi(id);
        if (res?.response?.status >= 400) {
            flash(readErr(res, locale.payeeAdmin.tables.validation.archiveFailed), 'error');
            return;
        }
        setReload((n) => n + 1);
        flash(locale.payeeAdmin.tables.validation.archived, 'success');
    };

    const unarchiveTable = async (id: number) => {
        const res = await calcTableUnarchiveApi(id);
        if (res?.response?.status >= 400) {
            flash(readErr(res, locale.payeeAdmin.tables.validation.unarchiveFailed), 'error');
            return;
        }
        setReload((n) => n + 1);
        flash(locale.payeeAdmin.tables.validation.unarchived, 'success');
    };

    const removeTable = async (id: number) => {
        const res = await calcTableDeleteApi(id);
        if (res?.response?.status >= 400) {
            flash(readErr(res, locale.common.deleteFailed), 'error');
            setConfirmDeleteTableId(null);
            return;
        }
        setConfirmDeleteTableId(null);
        setReload((n) => n + 1);
        flash(locale.payeeAdmin.tables.validation.deleted, 'success');
    };

    const makeDefault = async () => {
        if (!selected) return;
        const res = await payeeSetDefaultApi(selected.id);
        if (res?.response?.status >= 400) {
            flash(readErr(res, locale.payeeAdmin.validation.makeDefaultFailed), 'error');
            return;
        }
        setReload((n) => n + 1);
        flash(locale.payeeAdmin.validation.madeDefault, 'success');
    };

    const remove = async () => {
        if (!selected) return;
        const res = await payeeDeleteApi(selected.id);
        if (res?.response?.status >= 400) {
            flash(readErr(res, locale.common.deleteFailed), 'error');
            setConfirmDelete(false);
            return;
        }
        setConfirmDelete(false);
        setSelectedId(null);
        setReload((n) => n + 1);
        flash(locale.payeeAdmin.validation.deleted, 'success');
    };

    const upload = async (kind: 'logo' | 'stamp' | 'sign', fileList: FileList | null) => {
        if (!selected || !fileList || !fileList[0]) return;
        const res = await payeeUploadImageApi(selected.id, kind, fileList[0]);
        if (res?.response?.status >= 400) {
            flash(readErr(res, locale.payeeAdmin.validation.uploadFailed), 'error');
            return;
        }
        setReload((n) => n + 1);
        flash(
            kind === 'logo'
                ? locale.payeeAdmin.validation.logoUpdated
                : kind === 'stamp'
                    ? locale.payeeAdmin.validation.stampUpdated
                    : locale.payeeAdmin.validation.signUpdated,
            'success',
        );
    };

    // Tabs over the current payee's calc tables — explicit filters instead of
    // one long undifferentiated list.
    const currentTableId = selected?.currentCalcTable?.id ?? null;
    const archivedTablesCount = calcTables.filter((t) => t.isArchived).length;
    const TABLE_TABS: Array<{ value: typeof tableFilter, label: string, count: number }> = [
        { value: 'all', label: locale.payeeAdmin.tables.tabs.all, count: calcTables.length },
        { value: 'current', label: locale.payeeAdmin.tables.tabs.current, count: currentTableId ? 1 : 0 },
        { value: 'archived', label: locale.payeeAdmin.tables.tabs.archived, count: archivedTablesCount },
    ];
    const visibleTables = calcTables.filter((t) => {
        if (tableFilter === 'current') return t.id === currentTableId;
        if (tableFilter === 'archived') return t.isArchived;
        return true;
    });

    const previewConfig: CalcConfig | null = tableEditor ? {
        vatRate: Number(tableEditor.vatRate) || 0,
        epRate: Number(tableEditor.epRate) || 0,
        militaryRate: Number(tableEditor.militaryRate) || 0,
        schema: tableEditor.schema,
        footer: tableEditor.footer,
    } : null;

    return (
        <div className='admin page-content-container'>
            <NavPanel/>
            <div className='admin__body page_content_body'>

                <div className="admin__users-container contentWindow">
                    <div className="admin__data-header contentWindow__header">
                        <BackButton to={URLS.ADMIN_URL}/>
                        <span className='contentWindow__name'>{locale.payeeAdmin.pageTitle}</span>
                    </div>
                    <nav className="admin__data-nav contentWindow__nav">
                        <button className="nav__button btn-primary"
                                onClick={() => { setIsCreating(true); setSelectedId(null); }}>
                            {locale.payeeAdmin.createButton}
                        </button>
                    </nav>
                    <div className="admin__data-body-users-list contentWindow__body">
                        {payees.map((p) => (
                            <button key={p.id}
                                    className={`admin__user-button payee__list-button${!isCreating && selectedId === p.id ? ' payee__list-button-active' : ''}`}
                                    onClick={() => { setIsCreating(false); setSelectedId(p.id); }}>
                                <span className='payee__list-name'>{p.name}</span>
                                <span className='payee__list-num' title={locale.payeeAdmin.invoiceCounterBadgeTitle}>№{p.invoiceCounter}</span>
                                {p.isDefault && <span className='payee__badge'>{locale.payeeAdmin.defaultBadge}</span>}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="admin__content-container contentWindow payee-page">
                    <div className="admin__data-header contentWindow__header">
                        <span className='contentWindow__name'>
                            {isCreating ? locale.payeeAdmin.newPayeeHeader : (selected ? selected.name : locale.common.emptyDash)}
                        </span>
                    </div>

                    <nav className="admin__data-nav contentWindow__nav payee__toolbar">
                        {(isCreating || selected) && (
                            <button type='submit' form='payee-form' className="nav__button btn-primary"
                                    title={locale.payeeAdmin.toolbar.saveRequisitesTitle}>
                                {isCreating ? locale.payeeAdmin.createButton : locale.payeeAdmin.toolbar.saveRequisites}
                            </button>
                        )}
                        {isCreating && (
                            <button type='button' className="nav__button btn-ghost"
                                    onClick={() => { setIsCreating(false); setSelectedId(payees[0]?.id ?? null); }}>
                                {locale.common.cancel}
                            </button>
                        )}
                        {!isCreating && selected && !selected.isDefault && (
                            <button className="nav__button btn-ghost" onClick={makeDefault}>
                                {locale.payeeAdmin.toolbar.makeDefault}
                            </button>
                        )}
                        {!isCreating && selected && !confirmDelete && (
                            <button className="nav__button btn-danger" onClick={() => setConfirmDelete(true)}
                                    disabled={selected.isDefault}
                                    title={selected.isDefault ? locale.payeeAdmin.toolbar.deleteDefaultBlockedTitle : ''}>
                                {locale.common.delete}
                            </button>
                        )}
                        {!isCreating && selected && confirmDelete && (
                            <>
                                <button className="nav__button btn-danger" onClick={remove}>{locale.common.confirm}</button>
                                <button className="nav__button btn-ghost" onClick={() => setConfirmDelete(false)}>{locale.common.cancel}</button>
                            </>
                        )}
                        {msg && (
                            <span className='payee__msg'
                                  style={{ color: msg.type === 'error' ? 'var(--color-danger)' : 'var(--color-success)' }}>
                                {msg.text}
                            </span>
                        )}
                    </nav>

                    <div className="contentWindow__body payee__body">
                        {(isCreating || selected) ? (
                            <div className='payee__grid'>
                                <form id='payee-form' className='payee__form' onSubmit={submit}>
                                    {FIELDS.map(({ key, label, placeholder, type, hint, warn, maxLength }) => {
                                        const counterMin = isCreating ? 0 : (selected?.invoiceCounter ?? 0);
                                        return (
                                        <label key={key} className='payee__label'>
                                            <span>{label}{key === 'invoiceCounter' && !isCreating ? locale.payeeAdmin.invoiceCounterMinSuffix(counterMin) : ''}</span>
                                            <input className='input'
                                                   type={type || 'text'}
                                                   min={key === 'invoiceCounter' ? counterMin : (type === 'number' ? 0 : undefined)}
                                                   value={form[key] as string}
                                                   placeholder={placeholder}
                                                   maxLength={type === 'number' ? undefined : maxLength}
                                                   onChange={(e) => setField(key, e.target.value)}/>
                                            {hint && <span className='payee__field-hint'>{hint}</span>}
                                            {warn && <span className='payee__field-warning'>⚠ {warn}</span>}
                                        </label>
                                        );
                                    })}

                                    <label className='payee__checkbox'>
                                        <input type='checkbox'
                                               checked={form.isVatPayer}
                                               onChange={(e) => setField('isVatPayer', e.target.checked)}/>
                                        <span>{locale.payeeAdmin.vatPayerCheckbox}</span>
                                    </label>

                                    {/* submit lives in the always-visible toolbar (button[form="payee-form"]);
                                        this hidden one keeps Enter-to-submit working inside the form */}
                                    <button type='submit' style={{ display: 'none' }} aria-hidden='true' tabIndex={-1}/>
                                </form>

                                <div className='payee__images'>
                                    {isCreating ? (
                                        <p className='payee__hint'>
                                            {locale.payeeAdmin.images.uploadHintBeforeCreate}
                                        </p>
                                    ) : selected && (
                                        <>
                                            <div className='payee__image-slot'>
                                                <span className='payee__image-title'>{locale.payeeAdmin.images.logoLabel}</span>
                                                {mediaUrl(selected.logoFile)
                                                    ? <img src={mediaUrl(selected.logoFile) as string} alt={locale.payeeAdmin.images.logoAlt}/>
                                                    : <div className='payee__image-empty'>{locale.common.noFile}</div>}
                                                <input ref={logoInput} type='file' accept='image/*'
                                                       style={{ display: 'none' }}
                                                       onChange={(e) => upload('logo', e.target.files)}/>
                                                <button className='nav__button btn-ghost'
                                                        onClick={() => logoInput.current?.click()}>
                                                    {locale.common.upload}
                                                </button>
                                            </div>
                                            <div className='payee__image-slot'>
                                                <span className='payee__image-title'>{locale.payeeAdmin.images.stampLabel}</span>
                                                {mediaUrl(selected.stampFile)
                                                    ? <img src={mediaUrl(selected.stampFile) as string} alt={locale.payeeAdmin.images.stampAlt}/>
                                                    : <div className='payee__image-empty'>{locale.common.noFile}</div>}
                                                <input ref={stampInput} type='file' accept='image/png,image/jpeg'
                                                       style={{ display: 'none' }}
                                                       onChange={(e) => upload('stamp', e.target.files)}/>
                                                <button className='nav__button btn-ghost'
                                                        onClick={() => stampInput.current?.click()}>
                                                    {locale.common.upload}
                                                </button>
                                                <label className='payee__image-width'>
                                                    <span>Ширина в счёте, px</span>
                                                    <input className='input' type='number' min={20} max={600}
                                                           placeholder='160'
                                                           value={form.stampWidthPx}
                                                           onChange={(e) => setField('stampWidthPx', e.target.value)}/>
                                                </label>
                                            </div>
                                            <div className='payee__image-slot'>
                                                <span className='payee__image-title'>{locale.payeeAdmin.images.signLabel}</span>
                                                {mediaUrl(selected.signFile)
                                                    ? <img src={mediaUrl(selected.signFile) as string} alt={locale.payeeAdmin.images.signAlt}/>
                                                    : <div className='payee__image-empty'>{locale.common.noFile}</div>}
                                                <input ref={signInput} type='file' accept='image/png,image/jpeg'
                                                       style={{ display: 'none' }}
                                                       onChange={(e) => upload('sign', e.target.files)}/>
                                                <button className='nav__button btn-ghost'
                                                        onClick={() => signInput.current?.click()}>
                                                    {locale.common.upload}
                                                </button>
                                                <label className='payee__image-width'>
                                                    <span>Ширина в счёте, px</span>
                                                    <input className='input' type='number' min={20} max={600}
                                                           placeholder='120'
                                                           value={form.signWidthPx}
                                                           onChange={(e) => setField('signWidthPx', e.target.value)}/>
                                                </label>
                                            </div>
                                        </>
                                    )}
                                </div>
                            </div>
                        ) : (
                            <p className='payee__hint'>{locale.payeeAdmin.selectOrCreateHint}</p>
                        )}

                        {!isCreating && selected && (
                            <div className='payee__section'>
                                <h3 className='payee__section-title'>{locale.payeeAdmin.tables.sectionTitle}</h3>
                                <span className='payee__field-hint'>
                                    {locale.payeeAdmin.tables.sectionHint}
                                </span>

                                {calcTables.length === 0 && !tableEditor ? (
                                    <p className='payee__hint'>
                                        {locale.payeeAdmin.tables.noneYetHint}
                                    </p>
                                ) : (
                                    <div className='calcTable__tabs'>
                                        {TABLE_TABS.map((tab) => (
                                            <button key={tab.value} type='button'
                                                    className={`calcTable__tab${tableFilter === tab.value ? ' calcTable__tab-active' : ''}`}
                                                    onClick={() => setTableFilter(tab.value)}>
                                                {tab.label} <span className='calcTable__tab-count'>{tab.count}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}

                                {calcTables.length > 0 && visibleTables.length === 0 && (
                                    <p className='payee__hint'>{locale.payeeAdmin.tables.emptyFilterHint}</p>
                                )}

                                <div className='calcTable__list'>
                                    {visibleTables.map((t) => {
                                        const isCurrent = currentTableId === t.id;
                                        const statusClass = isCurrent ? ' calcTable__card-current'
                                            : t.isArchived ? ' calcTable__card-archived'
                                            : t.hasUsage ? ' calcTable__card-used' : ' calcTable__card-draft';
                                        return (
                                        <div key={t.id} className={`calcTable__card${statusClass}`}>
                                            <div className='calcTable__card-head'>
                                                <span className='payee__list-name'>{t.name}</span>
                                                {isCurrent && <span className='payee__badge'>{locale.payeeAdmin.tables.badge.current}</span>}
                                                {t.hasUsage && <span className='payee__badge'>{locale.payeeAdmin.tables.badge.used}</span>}
                                                {t.isArchived && <span className='payee__badge'>{locale.payeeAdmin.tables.badge.archived}</span>}
                                                {!t.hasUsage && !t.isArchived && !isCurrent && <span className='payee__badge'>{locale.payeeAdmin.tables.badge.draft}</span>}
                                            </div>
                                            <div className='calcTable__card-actions'>
                                                {!t.isArchived && !isCurrent && (
                                                    <button type='button' className='nav__button btn-ghost' onClick={() => activateTable(t.id)}>
                                                        {locale.payeeAdmin.tables.makeCurrentButton}
                                                    </button>
                                                )}
                                                <button type='button' className='nav__button btn-ghost' onClick={() => openEditTable(t)}>
                                                    {t.hasUsage ? locale.payeeAdmin.tables.renameButton : locale.payeeAdmin.tables.editButton}
                                                </button>
                                                {!t.isArchived ? (
                                                    <button type='button' className='nav__button btn-ghost'
                                                            disabled={isCurrent}
                                                            title={isCurrent ? locale.payeeAdmin.tables.archiveCurrentBlockedTitle : ''}
                                                            onClick={() => archiveTable(t.id)}>
                                                        {locale.payeeAdmin.tables.archiveButton}
                                                    </button>
                                                ) : (
                                                    <button type='button' className='nav__button btn-ghost' onClick={() => unarchiveTable(t.id)}>
                                                        {locale.payeeAdmin.tables.unarchiveButton}
                                                    </button>
                                                )}
                                                {!t.hasUsage && !isCurrent && (
                                                    confirmDeleteTableId === t.id ? (
                                                        <>
                                                            <button type='button' className='nav__button btn-danger' onClick={() => removeTable(t.id)}>{locale.common.confirm}</button>
                                                            <button type='button' className='nav__button btn-ghost' onClick={() => setConfirmDeleteTableId(null)}>{locale.common.cancel}</button>
                                                        </>
                                                    ) : (
                                                        <button type='button' className='nav__button btn-danger' onClick={() => setConfirmDeleteTableId(t.id)}>{locale.common.delete}</button>
                                                    )
                                                )}
                                            </div>
                                        </div>
                                        );
                                    })}
                                </div>

                                {!tableEditor ? (
                                    <div>
                                        <button type='button' className='nav__button btn-primary' onClick={openCreateTable}>
                                            {locale.payeeAdmin.tables.newTableButton}
                                        </button>
                                    </div>
                                ) : (
                                    <form className='payee__form' onSubmit={submitTable}>
                                        <div className='payee__toolbar'>
                                            <button type='submit' className='nav__button btn-primary'>{locale.payeeAdmin.tables.saveTableButton}</button>
                                            <button type='button' className='nav__button btn-ghost' onClick={() => setTableEditor(null)}>{locale.common.cancel}</button>
                                        </div>
                                        <label className='payee__label'>
                                            <span>{locale.payeeAdmin.tableEditor.nameLabel}</span>
                                            <input className='input' type='text' maxLength={128}
                                                   value={tableEditor.name}
                                                   onChange={(e) => setTableEditor((f) => f && ({ ...f, name: e.target.value }))}/>
                                        </label>

                                        {tableEditor.locked && (
                                            <span className='payee__field-warning'>
                                                {locale.payeeAdmin.tableEditor.lockedWarning}
                                            </span>
                                        )}

                                        <div className='payee__label'>
                                            <span>Колонки для счёта (PDF)</span>
                                            <span className='payee__field-hint'>
                                                Какие колонки этой таблицы попадают в таблицу товаров счёта-фактуры.
                                                «— авто —» — взять по ролям в схеме, иначе стандартные столбцы.
                                                Настройка не влияет на расчёт и меняется даже у используемой таблицы.
                                            </span>
                                        </div>
                                        <div className='payee__row-2'>
                                            {INVOICE_SLOTS.map(({ key, label }) => (
                                                <label key={key} className='payee__label'>
                                                    <span>{label}</span>
                                                    <select className='select'
                                                            value={tableEditor.invoiceColumns[key] ?? ''}
                                                            onChange={(e) => setTableInvoiceCol(key, e.target.value)}>
                                                        <option value=''>— авто —</option>
                                                        {tableEditor.schema.map((c) => (
                                                            <option key={c.key} value={c.key}>
                                                                {c.label ? `${c.label} (${c.key})` : c.key}
                                                            </option>
                                                        ))}
                                                    </select>
                                                </label>
                                            ))}
                                        </div>

                                        <div className='payee__label'>
                                            <span>{locale.payeeAdmin.tableEditor.visibilityLabel}</span>
                                            <span className='payee__field-hint'>
                                                {locale.payeeAdmin.tableEditor.visibilityHint}
                                            </span>
                                        </div>
                                        <div className='payee__visibility'>
                                            {tableEditor.schema.map((col, idx) => (
                                                <label key={col.key} className='payee__schema-toggle'>
                                                    <input type='checkbox'
                                                           checked={!col.hidden}
                                                           onChange={(e) => setTableCol(idx, { hidden: e.target.checked ? undefined : true })}/>
                                                    <span>{col.label ? `${col.label} (${col.key})` : col.key}</span>
                                                </label>
                                            ))}
                                        </div>

                                        {!tableEditor.locked && (
                                            <>
                                                <div className='payee__label'>
                                                    <span>{locale.payeeAdmin.tableEditor.ratesLabel}</span>
                                                    <span className='payee__field-hint'>
                                                        {locale.payeeAdmin.tableEditor.ratesHint}
                                                    </span>
                                                </div>
                                                {TABLE_RATE_FIELDS.map(({ key, label }) => (
                                                    <label key={key} className='payee__label'>
                                                        <span>{label}</span>
                                                        <input className='input' type='number' min={0} max={100} step='0.01'
                                                               value={tableEditor[key]}
                                                               onChange={(e) => setTableEditor((f) => f && ({ ...f, [key]: e.target.value }))}/>
                                                    </label>
                                                ))}

                                                <div className='payee__label'>
                                                    <span>{locale.payeeAdmin.tableEditor.schemaLabel}</span>
                                                    <span className='payee__field-hint'>
                                                        {locale.payeeAdmin.tableEditor.schemaHint(tableEditor.schema.map((c) => c.key).join(', '), FORMULA_CONSTS.join(', '))}
                                                    </span>
                                                </div>

                                                <div className='payee__schema'>
                                                    {tableEditor.schema.map((col, idx) => {
                                                        const totalRole = (col.roles || []).find((r) => TOTAL_ROLE_SET.has(r)) || '';
                                                        return (
                                                        <div key={col.key} className='payee__schema-row'>
                                                            <div className='payee__schema-line'>
                                                                <span className='payee__schema-key'
                                                                      title={locale.payeeAdmin.tableEditor.columnKeyTitle}>{col.key}</span>
                                                                <input className='input payee__schema-label'
                                                                       type='text' maxLength={64} placeholder={locale.payeeAdmin.tableEditor.labelPlaceholder}
                                                                       title={locale.payeeAdmin.tableEditor.columnLabelTitle}
                                                                       value={col.label}
                                                                       onChange={(e) => setTableCol(idx, { label: e.target.value })}/>
                                                                <select className='select' value={col.kind}
                                                                        title={locale.payeeAdmin.tableEditor.columnKindTitle}
                                                                        onChange={(e) => setTableCol(idx, { kind: e.target.value as any })}>
                                                                    <option value='input'>{locale.payeeAdmin.tableEditor.kindInputOption}</option>
                                                                    <option value='formula'>{locale.payeeAdmin.tableEditor.kindFormulaOption}</option>
                                                                </select>
                                                                <select className='select' value={col.group ?? ''}
                                                                        title={locale.payeeAdmin.tableEditor.columnGroupTitle}
                                                                        onChange={(e) => setTableCol(idx, { group: (e.target.value || undefined) as any })}>
                                                                    <option value=''>{locale.payeeAdmin.tableEditor.groupNoneOption}</option>
                                                                    <option value='expenses'>{locale.payeeAdmin.tableEditor.groupExpensesOption}</option>
                                                                    <option value='income'>{locale.payeeAdmin.tableEditor.groupIncomeOption}</option>
                                                                </select>
                                                                <select className='select' value={totalRole}
                                                                        title={locale.payeeAdmin.tableEditor.totalRoleTitle}
                                                                        onChange={(e) => setTableColTotalRole(idx, e.target.value)}>
                                                                    {TOTAL_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                                                                </select>
                                                                <button type='button' className='payee__schema-btn' title={locale.common.moveUp}
                                                                        disabled={idx === 0} onClick={() => moveTableCol(idx, -1)}>↑</button>
                                                                <button type='button' className='payee__schema-btn' title={locale.common.moveDown}
                                                                        disabled={idx === tableEditor.schema.length - 1} onClick={() => moveTableCol(idx, 1)}>↓</button>
                                                                <button type='button' className='payee__schema-btn payee__schema-btn-del' title={locale.common.delete}
                                                                        disabled={tableEditor.schema.length <= 1} onClick={() => removeTableCol(idx)}>×</button>
                                                            </div>
                                                            {col.kind === 'formula' && (
                                                                <input className='input payee__schema-formula'
                                                                       type='text' maxLength={512} placeholder={locale.payeeAdmin.tableEditor.formulaPlaceholder}
                                                                       title={locale.payeeAdmin.tableEditor.formulaTitle}
                                                                       value={col.formula ?? ''}
                                                                       onChange={(e) => setTableCol(idx, { formula: e.target.value })}/>
                                                            )}
                                                        </div>
                                                        );
                                                    })}
                                                </div>
                                                <div className='payee__schema-actions'>
                                                    <button type='button' className='nav__button btn-ghost' onClick={addTableCol}>{locale.payeeAdmin.tableEditor.addColumnButton}</button>
                                                    <button type='button' className='nav__button btn-ghost' onClick={resetTableSchema}>{locale.payeeAdmin.tableEditor.resetSchemaButton}</button>
                                                </div>
                                                {tableSchemaProblems.length > 0 && (
                                                    <div className='payee__field-warning'>
                                                        {tableSchemaProblems.map((p, i) => <div key={i}>⚠ {p}</div>)}
                                                    </div>
                                                )}

                                                <div className='payee__label'>
                                                    <span>{locale.payeeAdmin.footerEditor.sectionLabel}</span>
                                                    <span className='payee__field-hint'>
                                                        {locale.payeeAdmin.footerEditor.hint(
                                                            tableEditor.footer.length ? ` (${tableEditor.footer.map((f) => f.key).join(', ')})` : '',
                                                            FORMULA_CONSTS.join(', '),
                                                        )}
                                                    </span>
                                                </div>
                                                <div className='payee__schema'>
                                                    {tableEditor.footer.map((f, idx) => (
                                                        <div key={f.key} className='payee__schema-row'>
                                                            <div className='payee__schema-line'>
                                                                <span className='payee__schema-key'
                                                                      title={locale.payeeAdmin.footerEditor.keyTitle}>{f.key}</span>
                                                                <input className='input payee__schema-label'
                                                                       type='text' maxLength={64} placeholder={locale.payeeAdmin.tableEditor.labelPlaceholder}
                                                                       title={locale.payeeAdmin.footerEditor.labelTitle}
                                                                       value={f.label}
                                                                       onChange={(e) => setFooterField(idx, { label: e.target.value })}/>
                                                                <select className='select' value={f.kind}
                                                                        title={locale.payeeAdmin.footerEditor.kindTitle}
                                                                        onChange={(e) => setFooterField(idx, { kind: e.target.value as any })}>
                                                                    <option value='sum'>{locale.payeeAdmin.footerEditor.kindSumOption}</option>
                                                                    <option value='formula'>{locale.payeeAdmin.tableEditor.kindFormulaOption}</option>
                                                                </select>
                                                                {f.kind === 'sum' && (
                                                                    <select className='select' value={f.column ?? ''}
                                                                            title={locale.payeeAdmin.footerEditor.columnTitle}
                                                                            onChange={(e) => setFooterField(idx, { column: e.target.value })}>
                                                                        <option value=''>{locale.payeeAdmin.footerEditor.columnPlaceholder}</option>
                                                                        {tableEditor.schema.map((c) => (
                                                                            <option key={c.key} value={c.key}>{c.key} — {c.label}</option>
                                                                        ))}
                                                                    </select>
                                                                )}
                                                                <input className='input' style={{ width: '5em' }}
                                                                       type='text' maxLength={16} placeholder={locale.payeeAdmin.footerEditor.suffixPlaceholder}
                                                                       title={locale.payeeAdmin.footerEditor.suffixTitle}
                                                                       value={f.suffix ?? ''}
                                                                       onChange={(e) => setFooterField(idx, { suffix: e.target.value })}/>
                                                                <input className='payee__schema-color' type='color'
                                                                       title={locale.payeeAdmin.footerEditor.colorTitle}
                                                                       value={f.color ?? '#c9c9c9'}
                                                                       onChange={(e) => setFooterField(idx, { color: e.target.value })}/>
                                                                <button type='button' className='payee__schema-btn' title={locale.common.moveUp}
                                                                        disabled={idx === 0} onClick={() => moveFooterField(idx, -1)}>↑</button>
                                                                <button type='button' className='payee__schema-btn' title={locale.common.moveDown}
                                                                        disabled={idx === tableEditor.footer.length - 1} onClick={() => moveFooterField(idx, 1)}>↓</button>
                                                                <button type='button' className='payee__schema-btn payee__schema-btn-del' title={locale.common.delete}
                                                                        onClick={() => removeFooterField(idx)}>×</button>
                                                            </div>
                                                            {f.kind === 'formula' && (
                                                                <input className='input payee__schema-formula'
                                                                       type='text' maxLength={512} placeholder={locale.payeeAdmin.footerEditor.formulaPlaceholder}
                                                                       title={locale.payeeAdmin.footerEditor.formulaTitle}
                                                                       value={f.formula ?? ''}
                                                                       onChange={(e) => setFooterField(idx, { formula: e.target.value })}/>
                                                            )}
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className='payee__schema-actions'>
                                                    <button type='button' className='nav__button btn-ghost' onClick={addFooterField}>{locale.payeeAdmin.footerEditor.addFieldButton}</button>
                                                </div>
                                                {tableFooterProblems.length > 0 && (
                                                    <div className='payee__field-warning'>
                                                        {tableFooterProblems.map((p, i) => <div key={i}>⚠ {p}</div>)}
                                                    </div>
                                                )}
                                            </>
                                        )}

                                    {!tableEditor.locked && (
                                        <>
                                            <div className='payee__label'>
                                                <span>{locale.payeeAdmin.preview.label}</span>
                                                <span className='payee__field-hint'>
                                                    {locale.payeeAdmin.preview.hint}
                                                </span>
                                            </div>
                                            <div className='calcTable__preview'>
                                                <CalculationTable Key='payeeTablePreview' isEditable={true} calcConfig={previewConfig ?? undefined}/>
                                                <div id='payeeTablePreview-footer' className='calcTable__preview-footer'></div>
                                            </div>
                                        </>
                                    )}

                                        <div className='payee__toolbar'>
                                            <button type='submit' className='nav__button btn-primary'>{locale.payeeAdmin.tables.saveTableButton}</button>
                                            <button type='button' className='nav__button btn-ghost' onClick={() => setTableEditor(null)}>{locale.common.cancel}</button>
                                        </div>
                                    </form>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminPayees;
